import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/outline';

function About() {
    return (
        <section id="about" className="relative min-h-screen flex items-center overflow-hidden pt-16 lg:pt-20">
            {/* Animated Background Elements */}
            <div className="absolute inset-0">
                <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s'}}></div>
            </div>
            
            {/* Floating Particles */}
            <div className="absolute inset-0 overflow-hidden">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-2 h-2 bg-white/20 rounded-full animate-float"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 5}s`,
                            animationDuration: `${3 + Math.random() * 4}s`
                        }}
                    ></div>
                ))}
            </div>

            <div className="relative container mx-auto px-6 lg:px-12">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
                    {/* Content Section */}
                    <div className="flex-1 text-center lg:text-left space-y-8">
                        {/* Greeting */}
                        <div className="space-y-2">
                            <p className="text-white font-medium text-lg tracking-wide animate-fadeInUp">
                                👋 Hello, I'm
                            </p>
                            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight animate-fadeInUp" style={{animationDelay: '0.2s'}}>
                                <span className="text-white">
                                    Kaleb Abiy
                                </span>
                            </h1>
                        </div>

                        {/* Role/Title */}
                        <div className="animate-fadeInUp" style={{animationDelay: '0.4s'}}>
                            <h2 className="text-2xl lg:text-3xl font-semibold text-gray-200 mb-4">
                                Full-Stack Developer & 
                                <span className="text-blue-400"> Digital Innovator</span>
                            </h2>
                            <div className="w-24 h-1 bg-blue-500 mx-auto lg:mx-0 rounded-full"></div>
                        </div>

                        {/* Description */}
                        <p className="text-xl text-gray-300 leading-relaxed max-w-2xl animate-fadeInUp" style={{animationDelay: '0.6s'}}>
                            I build <span className="text-blue-400 font-semibold">Full Stack Applications</span> and 
                            <span className="text-blue-400 font-semibold"> intelligent AI agents</span> that solve real problems. 
                            From complex automation to scalable enterprise solutions.
                        </p>

                        {/* Tech Stack Pills */}
                        <div className="flex flex-wrap gap-3 justify-center lg:justify-start animate-fadeInUp" style={{animationDelay: '0.8s'}}>
                            {['AI Agents', 'React', 'Django', 'Python', 'Machine Learning', 'Microservices', 'PostgreSQL', 'Docker'].map((tech, index) => (
                                <span
                                    key={tech}
                                    className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-medium text-white hover:bg-white/20 transition-all duration-300 cursor-default"
                                    style={{animationDelay: `${0.9 + index * 0.1}s`}}
                                >
                                    {tech}
                                </span>
                            ))}
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fadeInUp" style={{animationDelay: '1s'}}>
                            <a
                                href="#contact"
                                className="group relative px-8 py-4 bg-blue-500 text-white font-semibold rounded-full hover:bg-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                            >
                                <span className="relative z-10">Let's Work Together</span>
                            </a>
                            <a
                                href="#projects"
                                className="group px-8 py-4 border-2 border-blue-500/50 text-blue-400 font-semibold rounded-full hover:border-blue-500 hover:bg-blue-500/10 transition-all duration-300 backdrop-blur-sm"
                            >
                                <span className="flex items-center gap-2">
                                    View My Work
                                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </span>
                            </a>
                        </div>
                    </div>

                    {/* Image Section */}
                    <div className="flex-1 max-w-lg animate-fadeInUp -mt-8 lg:mt-0" style={{animationDelay: '0.4s'}}>
                        <div className="relative">
                            {/* Glowing border effect */}
                            <div className="absolute -inset-1 bg-blue-500 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>
                            
                            {/* Main image container */}
                            <div className="relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-2 border border-white/10">
                                <img
                                    className="w-full h-auto object-cover rounded-xl shadow-2xl transition-transform duration-500 hover:scale-105"
                                    alt="Kaleb Abiy - Full Stack Developer"
                                    src="./opt2.jpg"
                                />
                                
                                {/* Floating badge */}
                                <div className="absolute -bottom-4 -right-4 bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg animate-bounce">
                                    Available for collaboration 🚀
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
                    <ChevronDownIcon className="w-6 h-6 text-white/60" />
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes float {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-20px);
                    }
                }
                
                .animate-fadeInUp {
                    animation: fadeInUp 0.8s ease-out forwards;
                    opacity: 0;
                }
                
                .animate-float {
                    animation: float 6s ease-in-out infinite;
                }
            `}</style>
        </section>
    )
}

export default About



// WEBPACK FOOTER //
// ./src/components/About.js